'use client'

import * as React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faX } from '@fortawesome/free-solid-svg-icons/faX'
import { STYLE_NAME } from '@/lib/constants'
import { type EnumStyle } from '../../../../../../../../../../lib/database/lib/generated/enums'
import { useAppDispatch, useAppSelector } from '@/lib/store'
import { getStyles } from '@/lib/features/styles/stylesSlice'
import { getBrowseFiltersStyle } from '@/lib/features/browseFilters/browseFiltersSlice'

export default function BrowsePageStyleMenu(): React.ReactElement {
  const dispatch = useAppDispatch()
  const styles = useAppSelector(getStyles) ?? []
  const selectedStyle = useAppSelector(getBrowseFiltersStyle)

  const [isMenuOpen, setIsMenuOpen] = React.useState<boolean>(false)
  const [searchTerm, setSearchTerm] = React.useState<string>('')

  const menuRef = React.useRef<HTMLDivElement | null>(null)
  const menuButtonRef = React.useRef<HTMLButtonElement | null>(null)
  const searchStylesRef = React.useRef<HTMLInputElement | null>(null)

  React.useEffect(() => {
    if (!isMenuOpen) {
      setSearchTerm('')
    } else {
      searchStylesRef.current?.focus()
    }
  }, [isMenuOpen])

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (isMenuOpen) {
        if (
          menuRef.current &&
          !menuRef.current.contains(event.target as Node) &&
          menuButtonRef.current &&
          !menuButtonRef.current.contains(event.target as Node)
        ) {
          setIsMenuOpen(false)
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isMenuOpen, menuRef])

  return (
    <div className="relative flex flex-col">
      <label htmlFor="style" className="text-primary-text dark:text-primary-text-dark block">
        <button
          ref={menuButtonRef}
          id="style"
          className={`button-small ${selectedStyle ? 'button-default-outline' : 'button-default-outline-faded'} inline-flex h-[30px] gap-0 p-0`}
          type="button"
          onClick={() => {
            setIsMenuOpen(!isMenuOpen)
          }}
        >
          <div className={`ml-2.5 ${!selectedStyle ? 'mr-2.5' : ''}`}>
            {`Style: ${STYLE_NAME[selectedStyle?.code as keyof typeof EnumStyle] ?? 'Any'}`}
          </div>
          {selectedStyle && (
            <FontAwesomeIcon
              icon={faX}
              className="ml-2 size-2.5 rounded-r-md p-2.5 hover:bg-neutral-500/5 dark:hover:hover:bg-neutral-500/25"
              onClick={(event: React.MouseEvent<SVGSVGElement>) => {
                void (async () => {
                  const { setBrowseFiltersPage, setBrowseFiltersStyle } = await import('@/lib/features/browseFilters/browseFiltersSlice')

                  event.preventDefault()
                  event.stopPropagation()

                  dispatch(setBrowseFiltersStyle(null))
                  dispatch(setBrowseFiltersPage(1))
                })()
              }}
            />
          )}
        </button>
      </label>
      <div
        ref={menuRef}
        className={`z-10 ${!isMenuOpen ? 'hidden' : ''} shadow-shadow dark:shadow-shadow-dark absolute top-[40px] w-64 divide-y divide-neutral-200 rounded-lg bg-white shadow-lg dark:divide-neutral-600 dark:bg-neutral-700`}
      >
        <div className="relative my-2 px-2">
          <div className="pointer-events-none absolute inset-y-0 start-2 flex items-center ps-3">
            <svg
              className="text-disabled-text dark:text-secondary-text-dark size-4"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 20 20"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
              />
            </svg>
            <span className="sr-only">Search icon</span>
          </div>
          <input
            ref={searchStylesRef}
            type="text"
            value={searchTerm}
            className="input box-border w-full ps-10"
            placeholder="Search styles..."
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setSearchTerm(event.target.value)
            }}
          />
        </div>
        <ul
          className="text-secondary-text dark:text-secondary-text-dark max-h-64 overflow-y-auto py-2 text-sm"
          aria-labelledby="dropdownDefaultButton"
        >
          {styles
            .filter((item) => !searchTerm || item.code.toLowerCase().includes(searchTerm.toLowerCase()))
            .sort((styleA, styleB) => styleA.sort_order - styleB.sort_order)
            .map((item) => (
              <li
                key={item.code}
                role="presentation"
                className="flex cursor-pointer justify-between hover:bg-neutral-100 dark:hover:bg-neutral-600"
                onClick={() => {
                  void (async () => {
                    const { setBrowseFiltersPage, setBrowseFiltersStyle } = await import('@/lib/features/browseFilters/browseFiltersSlice')

                    dispatch(setBrowseFiltersStyle(item))
                    dispatch(setBrowseFiltersPage(1))
                    setIsMenuOpen(false)
                  })()
                }}
              >
                <div className="block px-4 py-2">
                  <span>{STYLE_NAME[item.code as keyof typeof EnumStyle]}</span>
                </div>
              </li>
            ))}
          {styles.filter((item) => !searchTerm || item.code.toLowerCase().includes(searchTerm.toLowerCase())).length === 0 && (
            <li role="presentation" className="flex cursor-default justify-between">
              <div className="block px-4 py-2">
                <span>No matching styles</span>
              </div>
            </li>
          )}
        </ul>
      </div>
    </div>
  )
}
